import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";

const SealCharts = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const API_URL = "https://nft.rolandstudios.com/fetch_sealstats.php";

  useEffect(() => {
    async function fetchLeaderboard() {
      try {
        const response = await axios.get(API_URL);
        if (response.data.leaderboard?.entries) {
          setLeaderboard(response.data.leaderboard.entries);
        } else {
          console.warn("No leaderboard entries found.");
        }
      } catch (error) {
        console.error("Error fetching leaderboard:", error);
      }
    }
    fetchLeaderboard();
  }, []);

  const maxPage = Math.ceil(leaderboard.length / pageSize) || 1;

  const getCurrentPageData = () => {
    const start = page * pageSize;
    const current = leaderboard.slice(start, start + pageSize);
    while (current.length < pageSize) {
      current.push({
        rank: start + current.length + 1,
        name: "TBD",
        country: "N/A",
        score: "000",
        avatar: null,
      });
    }
    return current;
  };

  const handlePageChange = (offset) => {
    setPage(Math.max(0, Math.min(page + offset, maxPage - 1)));
  };

  return (
    <BackgroundContainer>
      <ChartWrapper>
        <h1>FLIPPER CHARTS</h1>

        <TableWrapper>
          <StyledTable>
            <thead>
              <TableHeaderRow>
                <StyledTh>Rank</StyledTh>
                <StyledTh>Player</StyledTh>
                <StyledTh>Country</StyledTh>
                <StyledTh>Elo</StyledTh>
              </TableHeaderRow>
            </thead>
            <tbody>
              {getCurrentPageData().map((entry, index) => (
                <StaggeredRow
                  key={index}
                  even={index % 2 === 0}
                  className="fade-in-up"
                  style={{ "--index": index }}
                >
                  <StyledTd>{entry.rank}</StyledTd>
                  <StyledTd>
                    <PlayerContainer>
                      {entry.avatar ? (
                        <Avatar src={entry.avatar} alt="Avatar" />
                      ) : (
                        <PlaceholderAvatar />
                      )}
                      <PlayerName>{entry.name}</PlayerName>
                    </PlayerContainer>
                  </StyledTd>
                  <StyledTd>
                    {entry.country !== "N/A" ? (
                      <FlagIcon
                        src={`https://flagcdn.com/w40/${entry.country.toLowerCase()}.png`}
                        alt={entry.country}
                      />
                    ) : (
                      "N/A"
                    )}
                  </StyledTd>
                  <StyledTd>{entry.score}</StyledTd>
                </StaggeredRow>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>

        <BottomControls>
          <PageButton onClick={() => handlePageChange(-1)} disabled={page === 0}>
            ◀
          </PageButton>
          <PageText>
            Page {page + 1} of {maxPage}
          </PageText>
          <PageButton onClick={() => handlePageChange(1)} disabled={page >= maxPage - 1}>
            ▶
          </PageButton>
        </BottomControls>
      </ChartWrapper>
    </BackgroundContainer>
  );
};

export default SealCharts;

// --- STYLES ---

const BackgroundContainer = styled.div`
  width: 100%;
  background: transparent;
  padding: 0;
`;

const ChartWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  border-radius: 20px;
  background: rgba(42, 151, 205, 0);
  text-align: center;

  h1 {
    color: rgb(255, 249, 87);
    font-size: 50px;
    font-weight: bold;
    font-family: 'Caveat Brush', cursive;
    text-shadow: 4px 4px 8px rgba(0, 0, 0, 0.6);
  }
`;

const TableWrapper = styled.div`
  overflow-x: hidden;
  overflow-y: hidden; /* 👈 NEW */
  border-radius: 20px;
  position: relative;
`;
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed; /* Forces even column distribution */
`;


const TableHeaderRow = styled.tr`
  background: rgba(89, 183, 220, 0.35);
  backdrop-filter: blur(6px);
`;

const StyledTh = styled.th`
  color: #fffa87;
  padding: 12px;
  font-size: 20px;
  font-family: 'Caveat Brush', cursive;
  text-align: left;
  background: transparent;
  text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:nth-child(1) { width: 12%; }
  &:nth-child(2) { width: 44%; }
  &:nth-child(3) { width: 22%; }
  &:nth-child(4) { width: 22%; }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const StyledTd = styled.td`
  padding: 12px;
  text-align: left;
  font-family: 'Caveat Brush', cursive;
  color: #e8f7ff;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;


const StaggeredRow = styled.tr`
  position: relative; /* 👈 critical to prevent scroll */
  background-color: ${(props) =>
    props.even
      ? "rgba(150, 213, 240, 0.69)"
      : "rgba(114, 199, 225, 0.69)"};
  box-shadow: inset 0 0 4px rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(4px);
  opacity: 0;
  transform: translateY(120px);
  animation: fadeUp 0.9s ease-out forwards;
  animation-delay: calc(0.1s * var(--index));

  @keyframes fadeUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;




const PlayerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PlayerName = styled.span`
  font-size: 18px;
  font-family: 'Caveat Brush', cursive;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid yellow;
  box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.5);
`;

const PlaceholderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 2px dashed yellow;
`;

const FlagIcon = styled.img`
  width: 32px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #fff;
  box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.5);
`;

const BottomControls = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
`;

const PageText = styled.p`
  font-size: 20px;
  color: white;
  font-family: 'Caveat Brush', cursive;
`;

const PageButton = styled.button`
  padding: 6px 14px;
  font-size: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid yellow;
  color: yellow;
  font-family: 'Caveat Brush', cursive;
  border-radius: 10px;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
